import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import ImportFileCSV from 'Components/Organisms/ImportFileCSV/ImportFileCSV';
import { importContacts, suggestContactsMapping } from 'Services/invoices';
import { DOCUMENTATION_LINKS } from 'Utils/constants';
import Button from 'Components/Atoms/Button/Button';
import OutlineDocs from 'Components/Atoms/OutlineIcons/OutlineDocs';
import OutlineImport from 'Components/Atoms/OutlineIcons/OutlineImport';
import useDrawerStack from 'Hooks/useDrawerStack';
import useDrawerStackRegister from 'Hooks/useDrawerStackRegister';
import ContactsImportMapperDrawer from './ContactsImportMapperDrawer';
import ContactsImportsHistoryDrawer from './ContactsImportsHistoryDrawer';
import contactsCSVImage from './contacts-csv.svg';

const DRAWER_CONTACTS_IMPORTS_HISTORY = 'contacts-imports-history';
const DRAWER_CONTACTS_IMPORT_MAPPER = 'contacts-import-mapper';

export default function ContactsImportCSVDrawer({ onClose }) {
    const [processing, setProcessing] = useState(false);
    const { t, i18n } = useTranslation('contacts');
    const queryClient = useQueryClient();

    const { open } = useDrawerStack();
    useDrawerStackRegister([
        [DRAWER_CONTACTS_IMPORTS_HISTORY, ContactsImportsHistoryDrawer],
        [DRAWER_CONTACTS_IMPORT_MAPPER, ContactsImportMapperDrawer],
    ]);

    const documentationLink = DOCUMENTATION_LINKS.IMPORT_CONTACTS[i18n.language];

    const handleSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
        onClose();
    };

    const handleOpenImportsHistory = () => {
        open(DRAWER_CONTACTS_IMPORTS_HISTORY, {
            title: t('imports-history-title'),
        });
    };

    const handleMapping = ({ importId, columns, suggestion }) => {
        open(DRAWER_CONTACTS_IMPORT_MAPPER, {
            title: t('import-mapper-title'),
            importId,
            columns,
            suggestion,
        });
    };

    return (
        <div className="h-full pb-4 flex flex-col">
            <div className="grid gap-2 mb-4 w-full">
                <div className="flex flex-wrap gap-2">
                    <Button size="small" target="_blank" href={documentationLink}>
                        <OutlineDocs size={18} />
                        {t('see-documentation')}
                    </Button>
                    <Button size="small" onClick={handleOpenImportsHistory}>
                        <OutlineImport size={18} />
                        {t('past-imports')}
                    </Button>
                </div>
            </div>

            <ImportFileCSV
                onClose={onClose}
                importService={importContacts}
                suggestService={suggestContactsMapping}
                processing={processing}
                onChangeProcessing={setProcessing}
                onSuccess={handleSuccess}
                onMapping={handleMapping}
            >
                <div className="flex flex-col items-center justify-center grow text-gray-500 gap-3">
                    <img src={contactsCSVImage} alt="" className="h-16 w-auto" />
                    <div className="text-center">{t('drop-csv-xlsx')}</div>
                </div>
            </ImportFileCSV>
        </div>
    );
}

ContactsImportCSVDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
};
